/**
 * The record page contract, as code.
 *
 * `<RecordPage>` is the frame; this is the furniture that goes in it. Every
 * record in Rowted has the same seven-ish tabs in the same order carrying the
 * same things — Overview, Activity, whichever related lists it has, Files, its
 * own tabs, History — and before this file each page wrote that list out by
 * hand and then wrote a `switch` to render it. Two pages had already drifted:
 * a stray tab order here, a different empty message for the same list there.
 *
 * So the order, the icons, the counts, the wide flag, the default columns and
 * the empty copy live in one place, and a page says what it *has* rather than
 * how to draw it:
 *
 *   const related = [
 *     { area: 'visits', listKey: 'visits@job', baseFilter: [...] },
 *     { area: 'invoices', listKey: 'invoices@job', baseFilter: [...] },
 *   ]
 *
 *   tabs: (record, summary) => standardTabs({
 *     counts: summary?.counts ?? {}, related, own: [siteTab],
 *   })
 *
 *   // …and in the page body
 *   {(tab) => renderStandardTab(tab, props) ?? <MyOwnTab />}
 *
 * The same `related` array feeds both, which is what stops the tab row and the
 * tab body disagreeing — the failure this file exists to make impossible.
 *
 * **What is deliberately not here.** The rail, the header, the mark, the
 * actions and the phone bar stay in each page's config, because those are the
 * parts that are genuinely about *this* object: a property's mark is a map, a
 * contact's is a photo, a job's is its status. Sharing them would mean a
 * configuration language for drawing, and a configuration language for drawing
 * is a worse React.
 *
 * See `docs/record-page-contract.md` for the rules a new module follows.
 */
import { useState, type ReactNode } from 'react'
import type { FileType, FilterCondition, RecordType } from '@rowted/shared'
import { RelatedList } from '../components/related-list.js'
import type { ListArea } from '../lib/use-list.js'
import type { request } from '../lib/api.js'
import { HistoryTab } from '../components/history-tab.js'
import { RecordActivityTab } from './record-activity.js'
import { RecordFilesTab } from './record-files.js'
import {
  NextUpCard, JobsPeek, NotesPeek, OverviewEmpty, OverviewSkeleton, VisitsPeek,
} from './record-overview.js'
import {
  OverviewDrop, QuietOverview, SetupCard, isQuietSummary, setQuietHidden, shouldShowSetup,
  type QuietConfig, type QuietStep,
} from './record-quiet.js'
import { useRecordUpload } from './use-record-upload.js'
import { Toast } from './../components/overlays.js'
import { usePhone } from '../lib/use-narrow.js'
import type { RecordTab } from './record-tabs.js'
import type { RecordSummary } from '../lib/use-record.js'

/**
 * The lists a record can have a tab of.
 *
 * `ListArea` rather than the original closed three, because Rowted Admin's
 * records hang lists off themselves too — a client's People, an admin's
 * Sessions — and they are the same thing: rows of another area narrowed to
 * this record. What keeps it honest is `RELATED` below plus `label`/`icon` on
 * the spec: an area that is not in the table must name itself, so a new tab
 * still cannot appear without somebody saying what it is called.
 */
export type RelatedArea = ListArea

export interface RelatedTabSpec {
  area: RelatedArea
  /**
   * What this tab is called, for an area `RELATED` does not know.
   *
   * The six tenant lists are in that table with their columns and their empty
   * copy; an admin area passes its own here rather than growing the table with
   * entries no tenant screen will ever read.
   */
  label?: string
  icon?: RecordTab['icon']
  title?: string
  emptyTitle?: string
  /** Shown even when the count is zero — a client with no support sessions. */
  always?: boolean
  /** Which request client the list uses. Admin areas pass `adminRequest`. */
  request?: typeof request
  /** What makes it this record's list. Never shown as a removable chip. */
  baseFilter: FilterCondition[]
  /**
   * `jobs@property` — what these settings are filed under. Per *kind* of
   * related list, not per record: "the jobs on a property" is one list people
   * want to look the same on every property.
   */
  listKey: string
  /** The columns it opens with. The default leaves out the obvious one. */
  fields?: string[]
  /** Why it is empty, in this record's words. */
  emptyBody?: string
  /** The New button. Omitted where this record cannot make one. */
  onNew?: { label: string; onClick: () => void }
}

/**
 * What each related list is called and opens with.
 *
 * The default columns leave out the column the record already is — a
 * property's jobs do not need a Property column — so a spec that wants one
 * back says so rather than restating the whole set.
 */
const RELATED: Partial<Record<RelatedArea, {
  label: string
  icon: RecordTab['icon']
  title: string
  fields: string[]
  emptyTitle: string
  emptyBody: string
}>> = {
  jobs: {
    label: 'Jobs',
    icon: 'jobs',
    title: 'Jobs',
    fields: ['title', 'status', 'nextAction', 'expected', 'createdAt'],
    emptyTitle: 'No jobs yet.',
    emptyBody: 'A job against this record shows up here.',
  },
  visits: {
    label: 'Visits',
    icon: 'visits',
    title: 'Work visits',
    /* Work first, day after it — the order every other list opens in
       (`VISITS_AREA.defaultFields`, `related-list.tsx`'s preset). Jobs
       and Invoices above already lead with what the row is; this led
       with the day, which the calendar mark beside it already says. */
    fields: ['description', 'scheduledDate', 'status', 'crewName', 'hoursForDay'],
    emptyTitle: 'No visits yet.',
    emptyBody: 'Scheduling work here puts its visits in this list.',
  },
  invoices: {
    label: 'Invoices',
    icon: 'invoices',
    title: 'Invoices',
    fields: ['reference', 'status', 'issueDate', 'amount', 'balance'],
    emptyTitle: 'No invoices yet.',
    emptyBody: 'An invoice covering this work shows up here.',
  },
}

/**
 * The tab row, in the one order every record uses.
 *
 * Overview · Activity · the related lists · Files · this object's own ·
 * History. Related tabs appear only when they have something in them, because
 * a tab that opens onto "No jobs yet" is a tab that cost somebody a click to
 * learn nothing. Overview, Activity, Files and History are always there: they
 * are where things are *put*, and a tab that vanishes when empty is a tab
 * nobody can find the first time they need it.
 */
export function standardTabs({ counts, related = [], lead = [], own = [], omit = [] }: {
  counts: Record<string, number>
  related?: RelatedTabSpec[]
  /**
   * This record's own tabs that **lead** — straight after Overview, before
   * Activity (`docs/visit-page.md` §7.1, do-not-substitute 3, D2).
   *
   * Additive and opt-in: the visit page passes its six (Materials · Equipment
   * · Time · Money · For the crew · From the crew) and nothing as `own`; no
   * other record passes it, so every other tab row is exactly as it was
   * (`tests/record-pages.test.ts` holds both).
   */
  lead?: RecordTab[]
  /** This record's own tabs, between Files and History. */
  own?: RecordTab[]
  /**
   * The always-there tabs this record has not got.
   *
   * Only Rowted Admin's records use it, and only because `activities`,
   * `files` and `audit_log` are tenant tables: a client is the row that owns
   * tenants, so there is nothing for those three to read. A tenant record
   * never omits them — "a tab that vanishes when empty is a tab nobody can
   * find the first time they need it" is still the rule, and this is about a
   * tab that could never fill, not one that happens to be empty.
   */
  omit?: ('activity' | 'files' | 'history')[]
}): RecordTab[] {
  const dropped = new Set(omit)
  const words = (spec: RelatedTabSpec) => RELATED[spec.area]
  return [
    { key: 'overview', label: 'Overview', icon: 'home' },
    ...lead,
    ...(dropped.has('activity')
      ? []
      : [{ key: 'activity', label: 'Activity', icon: 'note' as const, count: counts.activity }]),
    ...related
      /* `always` is for a list that is meaningful at zero — a client with no
         support sessions is a fact worth a tab, unlike a property with no
         jobs, which is just a click that teaches nothing. */
      .filter((spec) => spec.always || counts[spec.area])
      .map((spec) => ({
        key: spec.area,
        label: spec.label ?? words(spec)?.label ?? spec.area,
        icon: spec.icon ?? words(spec)?.icon ?? 'jobs',
        count: counts[spec.area],
        // The list engine does not fit beside a 340px rail (§3.5).
        wide: true,
      })),
    ...(dropped.has('files')
      ? []
      : [{ key: 'files', label: 'Files', icon: 'files' as const, count: counts.files }]),
    ...own,
    ...(dropped.has('history')
      ? []
      : [{ key: 'history', label: 'History', icon: 'clock' as const }]),
  ]
}

export interface StandardTabProps {
  object: RecordType
  id: string
  /** `/properties/abc` — where this record's own tabs live. */
  href: string
  summary: RecordSummary | null
  /** The same array `standardTabs` was given. */
  related?: RelatedTabSpec[]
  /** Who a task can be assigned to. One request for the page. */
  people: { id: string; name: string }[]
  orgUnitId: string
  /** The audit table History reads, plus anything stored off the record. */
  history: { table: string; related?: { tables: string[]; recordIds: string[] } }
  /** The activity being ticked off, so its box can go quiet. */
  busyId: string | null
  onComplete: (activityId: string) => void
  /** Anything that changes a count — a note added, a file removed. */
  onChanged: () => void
  /** Lends the page the Files tab's picker, for the green button on a phone. */
  onUploadReady: (open: () => void) => void
  canEdit: boolean
  onOpen: (path: string) => void
  /**
   * What this record's quiet page says — `docs/quiet-records.md` §2.
   *
   * `config.quiet?.build(record)`, passed through by the page. A record with
   * no quiet config keeps `OverviewEmpty`: nothing breaks for the ones that
   * have not opted in, which is what lets the configs land one record at a
   * time (§0, "Does not change").
   */
  quiet?: QuietConfig | null
  /**
   * Lends the page the quiet panel's log-activity sheet.
   *
   * The mirror of `onUploadReady`: on a phone the green button on Overview
   * opens that sheet while the record is quiet, and navigates to the Activity
   * tab once it is not (§4). The panel owns the sheet; the page owns the
   * button.
   *
   * **`null` on the way out**, unlike `onUploadReady` — which is the whole
   * reason the type differs. The panel unmounts the moment the first note
   * lands, and a page still holding its opener would raise a sheet belonging
   * to a component that is gone. Clearing it is what lets the button fall
   * back to the Activity tab without anybody having to ask a second time
   * whether the record is still quiet.
   */
  onLogReady?: (open: (() => void) | null) => void
  /** Which type chips the Files tab's empty state offers (§8, §11 item 4). */
  fileTypes?: FileType[]
}

/**
 * Draws the standard tabs, and returns `null` for anything else.
 *
 * `null` rather than a fallback so a page can add its own: `renderStandardTab(
 * tab, props) ?? <SiteAndAccess …>` reads as "the usual ones, and this one of
 * mine", and a typo in an own-tab's key shows an empty page rather than
 * silently landing on Overview.
 */
export function renderStandardTab(tab: string, props: StandardTabProps): ReactNode | null {
  const { object, id } = props

  switch (tab) {
    case 'overview':
      return <StandardOverview {...props} />

    case 'activity':
      return <ActivityTabDrop {...props} />

    case 'files':
      return (
        <RecordFilesTab
          object={object}
          id={id}
          orgUnitId={props.orgUnitId}
          {...(props.fileTypes ? { fileTypes: props.fileTypes } : {})}
          canEdit={props.canEdit}
          onChanged={props.onChanged}
          onReady={props.onUploadReady}
        />
      )

    case 'history':
      return (
        <HistoryTab
          table={props.history.table}
          recordId={id}
          {...(props.history.related ? { related: props.history.related } : {})}
        />
      )

    default: {
      const spec = props.related?.find((candidate) => candidate.area === tab)
      if (!spec) return null
      /*
       * The table's words, or the spec's. An admin area is not in `RELATED`
       * and says what it is called on the spec instead; a tenant area is in
       * the table and says nothing, which is what keeps six screens agreeing
       * on what "the jobs on a property" opens with.
       */
      const preset = RELATED[spec.area]
      return (
        <RelatedList
          area={spec.area}
          title={spec.title ?? preset?.title ?? spec.label ?? spec.area}
          listKey={spec.listKey}
          baseFilter={spec.baseFilter}
          {...(spec.fields ?? preset?.fields
            ? { fields: spec.fields ?? preset?.fields }
            : {})}
          emptyTitle={spec.emptyTitle ?? preset?.emptyTitle ?? 'Nothing here yet.'}
          emptyBody={spec.emptyBody ?? preset?.emptyBody ?? ''}
          variant="tab"
          {...(spec.request ? { request: spec.request } : {})}
          {...(spec.onNew ? { onNew: spec.onNew } : {})}
        />
      )
    }
  }
}

/**
 * The Activity tab, which takes a dropped photo as Overview does — its
 * composer says *@ to mention someone · drop a photo*, and until Aaron's
 * "yes" to the rich composer (Oct 25) only Overview's half of that was true:
 * a file dropped on this tab's box went nowhere. The same uploader, the same
 * overlay and the same describe dialog (`OverviewDrop`), and — like Overview
 * — only on a record that says what it is called (`quiet`).
 */
function ActivityTabDrop(props: StandardTabProps) {
  const uploader = useRecordUpload({
    object: props.object,
    id: props.id,
    orgUnitId: props.orgUnitId,
    canEdit: props.canEdit,
    onChanged: props.onChanged,
  })
  const tab = (
    <RecordActivityTab
      object={props.object}
      id={props.id}
      {...(props.quiet ? { subject: props.quiet.composerSubject } : {})}
      people={props.people}
      upcoming={props.summary?.nextUp ?? []}
      onComplete={props.onComplete}
      onChanged={props.onChanged}
      busyId={props.busyId}
    />
  )
  if (!props.quiet) return tab
  return (
    <OverviewDrop uploader={uploader} subject={props.quiet.composerSubject} object={props.object} id={props.id}
      onChanged={props.onChanged}>
      {tab}
    </OverviewDrop>
  )
}

/**
 * The Overview: what is next, what work is open, what is coming, what was
 * said — and an empty state until any of it exists.
 *
 * Every peek links back into a tab of this same record rather than off to the
 * module, because "view all the jobs on this property" is a tab away and
 * "view all jobs" is a different question.
 */
export function StandardOverview(props: StandardTabProps) {
  const {
    href, summary, related = [], busyId, onComplete, onOpen, quiet,
  } = props
  const phone = usePhone()
  /*
   * What the toast is about, for the five seconds it is up.
   *
   * It lives here rather than in `SetupCard` because the card is gone by the
   * time there is anything to say — Hide is the one action on a control that
   * removes the control, so the thing offering Undo has to outlive it.
   */
  const [hidden, setHidden] = useState(false)

  /*
   * **One uploader for the whole tab**, made here rather than in either
   * layout (§1 rule 8). Overview is the drop target in both of them — the
   * quiet panel says so on its dashed tile and the Set up card says so on
   * its last row, and a page where only one of those two is true is the
   * defect. `OverviewDrop` below is where it is spread.
   */
  const uploader = useRecordUpload({
    object: props.object,
    id: props.id,
    orgUnitId: props.orgUnitId,
    canEdit: props.canEdit,
    onChanged: props.onChanged,
  })
  const counts = summary?.counts ?? {}
  /*
   * A peek only for a list this record actually has.
   *
   * It is not cosmetic. `counts.jobs` on a *job* is 1 — itself, because a
   * job's related-job set is the job — so a peek that drew whatever the
   * summary happened to return would put the job you are reading in a card on
   * its own Overview under "Jobs · 1 open". The `related` array is the record
   * saying which lists are its, so that is what decides.
   */
  const has = (area: RelatedArea) => related.some((spec) => spec.area === area)

  /*
   * **Loading is its own answer, and it comes first** (`quiet-records.md` §9).
   *
   * This used to be `empty = !summary || (…counts…)` guarded by `empty &&
   * summary`, which reads as "empty, but only once we know" and behaves as
   * "draw the busy layout with nothing in it while we find out". Empty cards
   * on a record that is about to have cards look like the page working, so
   * nobody ever reported it — but once there is a quiet panel for the other
   * branch, the same hole flashes six first steps onto a record with four
   * hundred jobs on it.
   */
  if (!summary) return <OverviewSkeleton />

  if (isQuietSummary(summary, related)) {
    /*
     * The panel when this record has declared one, and the old one-button
     * empty state when it has not. `OverviewEmpty` is deliberately still
     * here and is the only place it is used (§14, item 12): the configs
     * arrive record by record, and a record page that has not got one yet
     * must not break.
     */
    return quiet
      ? (
        <OverviewDrop
          uploader={uploader}
          subject={quiet.composerSubject}
          object={props.object}
          id={props.id}
          onChanged={props.onChanged}
        >
          <QuietOverview
            quiet={quiet}
            object={props.object}
            id={props.id}
            people={props.people}
            uploader={uploader}
            onChanged={props.onChanged}
            {...(props.onLogReady ? { onLogReady: props.onLogReady } : {})}
          />
        </OverviewDrop>
      )
      : <OverviewEmpty onAdd={() => onOpen(`${href}?tab=activity`)} />
  }

  const busy = (
    <>
      <NextUpCard
        items={summary?.nextUp ?? []}
        busy={busyId}
        onComplete={onComplete}
        onAdd={() => onOpen(`${href}?tab=activity`)}
        onOpenAll={() => onOpen(`${href}?tab=activity`)}
        requestWithoutVisit={props.object === 'job' && summary?.requestWithoutVisit
          ? { jobId: props.id } : null}
      />

      {has('jobs') && (
        <JobsPeek
          jobs={summary?.peeks.jobs ?? []}
          total={counts.jobs ?? 0}
          onOpen={(jobId) => onOpen(`/jobs/${jobId}`)}
          onOpenAll={() => onOpen(`${href}?tab=jobs`)}
        />
      )}

      {has('visits') && (
        <VisitsPeek
          visits={summary?.peeks.visits ?? []}
          total={counts.visits ?? 0}
          onOpen={(visitId) => onOpen(`/visits/${visitId}`)}
          onOpenAll={() => onOpen(`${href}?tab=visits`)}
        />
      )}

      <NotesPeek
        notes={summary?.peeks.notes ?? []}
        onOpenAll={() => onOpen(`${href}?tab=activity`)}
      />

      {/*
        * After Notes, and last (board 5). The steps do not vanish when the
        * record stops being quiet — they shrink to this — and it goes by
        * itself at `6 of 6`, or when somebody presses Hide.
        */}
      {quiet && !hidden && shouldShowSetup(quiet, summary, related) && (
        <SetupCard
          quiet={quiet}
          phone={phone}
          onStep={(step: QuietStep) => {
            /*
             * The files step has no uploader here, so it goes to the Files
             * tab — which is a picker, a drop target and the list of what is
             * already attached, two inches above this card.
             */
            if (step.kind === 'files') onOpen(`${href}?tab=files`)
            else step.onSelect?.()
          }}
          onHide={() => {
            setHidden(true)
            void setQuietHidden(props.object, props.id, true).then(props.onChanged)
          }}
        />
      )}

      {hidden && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast
            onDismiss={() => setHidden(false)}
            action={{
              label: 'Undo',
              onClick: () => {
                setHidden(false)
                void setQuietHidden(props.object, props.id, false).then(props.onChanged)
              },
            }}
          >
            Set-up steps hidden — only for you. Bring them back from the ⋯ menu.
          </Toast>
        </div>
      )}
    </>
  )

  /*
   * The busy layout takes a drop too, and for the same reason — but only
   * where the record has a `quiet` config, because that is what knows what
   * to call itself in "Drop to attach to …". A record that has not opted in
   * behaves exactly as it did.
   */
  return quiet
    ? (
      <OverviewDrop
        uploader={uploader}
        subject={quiet.composerSubject}
        object={props.object}
        id={props.id}
        onChanged={props.onChanged}
      >
        {busy}
      </OverviewDrop>
    )
    : busy
}
